import React from "react";
import { Pagination, PaginationItem, PaginationLink, Input, Label } from "reactstrap";
import { ChevronLeft, ChevronRight } from "react-feather";

/**
 * UsersPagination - Pagination component for users view
 */
const UsersPagination = ({ pagination, onPageChange, onPageSizeChange }) => {
  const { currentPage, pageSize, totalPages, totalElements } = pagination;

  const handlePageClick = (page) => {
    if (page >= 0 && page < totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    onPageSizeChange(newSize);
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(0, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);

    // Adjust start if we're near the end
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(0, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  const startItem = currentPage * pageSize + 1;
  const endItem = Math.min((currentPage + 1) * pageSize, totalElements);

  return (
    <div className="d-flex justify-content-between align-items-center mt-3">
      {/* Info */}
      <div className="d-flex align-items-center gap-3">
        <span className="text-muted">
          Showing {startItem} to {endItem} of {totalElements} users
        </span>

        {/* Page Size Selector */}
        <div className="d-flex align-items-center gap-2">
          <Label for="pageSize" className="mb-0 text-muted">
            Per page:
          </Label>
          <Input
            id="pageSize"
            type="select"
            value={pageSize}
            onChange={handlePageSizeChange}
            style={{ width: "80px" }}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Input>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination>
          {/* Previous */}
          <PaginationItem disabled={currentPage === 0}>
            <PaginationLink
              previous
              onClick={() => handlePageClick(currentPage - 1)}
            >
              <ChevronLeft size={14} />
            </PaginationLink>
          </PaginationItem>

          {/* First page */}
          {pageNumbers[0] > 0 && (
            <>
              <PaginationItem>
                <PaginationLink onClick={() => handlePageClick(0)}>
                  1
                </PaginationLink>
              </PaginationItem>
              {pageNumbers[0] > 1 && (
                <PaginationItem disabled>
                  <PaginationLink>...</PaginationLink>
                </PaginationItem>
              )}
            </>
          )}

          {/* Page numbers */}
          {pageNumbers.map((page) => (
            <PaginationItem key={page} active={page === currentPage}>
              <PaginationLink onClick={() => handlePageClick(page)}>
                {page + 1}
              </PaginationLink>
            </PaginationItem>
          ))}

          {/* Last page */}
          {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
            <>
              {pageNumbers[pageNumbers.length - 1] < totalPages - 2 && (
                <PaginationItem disabled>
                  <PaginationLink>...</PaginationLink>
                </PaginationItem>
              )}
              <PaginationItem>
                <PaginationLink onClick={() => handlePageClick(totalPages - 1)}>
                  {totalPages}
                </PaginationLink>
              </PaginationItem>
            </>
          )}

          {/* Next */}
          <PaginationItem disabled={currentPage === totalPages - 1}>
            <PaginationLink
              next
              onClick={() => handlePageClick(currentPage + 1)}
            >
              <ChevronRight size={14} />
            </PaginationLink>
          </PaginationItem>
        </Pagination>
      )}
    </div>
  );
};

export default UsersPagination;
